import { useState, useEffect, useRef } from "react";
import { KeenIcon } from "@/components";
import { Modal } from "@/components/ui/modal";
import { Table, Input, Tooltip, Popconfirm, Checkbox } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import {
  faFileCsv,
  faMagnifyingGlass,
  faPenToSquare,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSuggestionData } from "../../../../../hooks/useSuggestionData";
import { useQueryClient } from "@tanstack/react-query";
import SuggestedModal from "./SuggestedModal";
import PropTypes from "prop-types";
import { TablePagination } from "../../../../../components/pagination/TablePagination";
import { convertToVNTime } from "../../../../../utils/Date";

const ManageSuggested = ({
  tableProps = {},
  open = false,
  onClose,
}) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // null for create, object for edit
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const intl = useIntl();

  // State để lưu trữ dữ liệu hiển thị
  const [localSuggestedData, setLocalSuggestedData] = useState({
    result: [],
    total: 0,
  });

  const [searchParams, setSearchParams] = useState({
    page: 1,
    limit: 5,
    suggest: "",
  });
  const [searchDraft, setSearchDraft] = useState({
    page: 1,
    limit: 5,
    suggest: "",
  });

  const {
    suggestionData,
    suggestionDataLoading,
    deleteSuggestedByIdAsync,
    importSuggestedFileAsync,
    createSuggestedAsync,
    updateSuggestedAsync,
  } = useSuggestionData(searchParams);

  // Cập nhật local state khi API data thay đổi
  useEffect(() => {
    if (suggestionData) {
      setLocalSuggestedData(suggestionData);
    }
  }, [suggestionData]);

  // Đồng bộ page state với searchParams.page
  useEffect(() => {
    setPage(searchParams.page);
  }, [searchParams.page]);

  useEffect(() => {
    if (
      !suggestionDataLoading &&
      localSuggestedData?.result?.length === 0 &&
      searchParams.page > 1
    ) {
      setSearchParams((prev) => ({
        ...prev,
        page: 1,
      }));
      const invalidateQueries = setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [
            "suggestion-data",
            searchParams?.page,
            searchParams?.limit,
            searchParams?.suggest,
          ],
        });
      }, 2000);
      return () => clearTimeout(invalidateQueries);
    }
  }, [localSuggestedData?.result?.length, suggestionDataLoading, searchParams.page]);

  // Xử lý các sự kiện
  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (item) => {
    try {
      await deleteSuggestedByIdAsync(item.id);
      setLocalSuggestedData((prev) => ({
        ...prev,
        result: prev.result.filter((resultItem) => resultItem.id !== item.id),
        total: (prev.total || 1) - 1,
      }));
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSearchParams((prev) => ({
      ...prev,
      ...searchDraft,
      page: newPage,
      limit: limit,
    }));
  };

  const handlePageSizeChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
    setSearchParams((prev) => ({
      ...prev,
      ...searchDraft,
      page: 1,
      limit: newLimit,
    }));
  };

  const handleSearchChange = (e, isNowEmpty = false) => {
    const { name, value } = e.target;
    setSearchDraft((prev) => {
      const nextDraft = { ...prev, [name]: value };
      if (isNowEmpty) {
        setPage(1);
        setSearchParams((prevParams) => ({
          ...prevParams,
          ...nextDraft,
          page: 1,
          limit: limit, // Cập nhật limit từ state hiện tại
        }));
      }
      return nextDraft;
    });
  };

  const handleSearch = () => {
    setSearchParams({
      ...searchParams,
      ...searchDraft,
      page: 1, // Reset về trang đầu tiên khi search
      limit: limit, // Cập nhật limit từ state hiện tại
    });
    setPage(1); // Cập nhật state page để UI đồng bộ
  };

  const handleCreate = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleImportCSV = () => {
    fileInputRef.current.click();
  };


  // Hàm parse toàn bộ nội dung CSV để xử lý đúng dữ liệu nhiều dòng
  const parseCSVContent = (text) => {
    const records = [];
    let currentRecord = [];
    let currentField = '';
    let inQuotes = false;
    let i = 0;
    
    while (i < text.length) {
      const char = text[i];
      
      if (char === '"') {
        if (inQuotes && text[i + 1] === '"') {
          // Escaped quote (double quotes)
          currentField += '"';
          i += 2; // Skip both quotes
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
          i++;
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        currentRecord.push(currentField);
        currentField = '';
        i++;
      } else if ((char === '\n' || char === '\r') && !inQuotes) {
        // End of record
        currentRecord.push(currentField);
        if (currentRecord.length > 0) {
          records.push(currentRecord);
        }
        currentRecord = [];
        currentField = '';
        // Skip \r\n combination
        if (char === '\r' && text[i + 1] === '\n') {
          i += 2;
        } else {
          i++;
        }
      } else {
        // Giữ nguyên mọi ký tự khác (bao gồm \n, \r trong quotes)
        currentField += char;
        i++;
      }
    }
    
    // Add the last field and record
    if (currentField !== '' || currentRecord.length > 0) {
      currentRecord.push(currentField);
      if (currentRecord.length > 0) {
        records.push(currentRecord);
      }
    }
    
    return records;
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (fileExtension !== "csv" && fileExtension !== "txt") {
      toast.error(intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.CSV_ONLY" }));
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      
      // Xử lý CSV đúng cách - parse toàn bộ file thành các record
      const records = parseCSVContent(text);
      if (records.length === 0) {
        toast.error(
          intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.EMPTY_FILE" })
        );
        return;
      }
      
      // Kiểm tra header
      const headers = records[0];
      if (headers[0].trim() !== "priority" || headers[1].trim() !== "suggest") {
        toast.error(
          intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.INVALID_HEADERS" })
        );
        return;
      }
      
      // Lọc các record hợp lệ (có giá trị ở cả 2 cột đầu)
      const validRecords = [records[0]];
      for (let i = 1; i < records.length; i++) {
        const record = records[i];
        if (
          record[0] &&
          record[1] &&
          record[0].trim() !== "" &&
          record[1].trim() !== ""
        ) {
          validRecords.push(record);
        }
      }
      if (validRecords.length === 1) {
        toast.error(
          intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.NO_VALID_ROWS" })
        );
        return;
      }
      
      // Tạo file CSV mới từ các record hợp lệ
      const newCsv = validRecords.map(record => 
        record.map(field => `"${field.replace(/"/g, '""')}"`).join(',')
      ).join('\n');
      const newFile = new File([newCsv], file.name, { type: file.type });

      try {
        const response = await importSuggestedFileAsync(newFile);
        const importedItems =
          response?.imported?.map((row) => {
            const { id, suggest, priority, created_at } = row;
            return {
              id: id,
              suggest,
              priority,
              created_at: created_at,
            };
          }) || [];

        setLocalSuggestedData((prev) => {
          const newTotal = (prev.total || 0) + importedItems.length;
          const currentPageSize = prev.result.length;

          // Nếu page hiện tại đã đầy, chuyển về page cuối để hiển thị dữ liệu mới
          if (currentPageSize >= limit) {
            const lastPage = Math.ceil(newTotal / limit);

            setSearchParams((prevParams) => ({
              ...prevParams,
              page: lastPage,
            }));
            const invalidateQueries = setTimeout(() => {
              queryClient.invalidateQueries({
                queryKey: [
                  "suggestion-data",
                  searchParams?.page,
                  searchParams?.limit,
                  searchParams?.suggest,
                ],
              });
            }, 2000);
            return () => clearTimeout(invalidateQueries);
          }

          // Nếu page hiện tại còn chỗ, thêm dữ liệu mới vào đầu
          return {
            ...prev,
            result: [...importedItems, ...prev.result],
            total: newTotal,
          };
        });
      } catch (error) {
        console.error("Lỗi khi import:", error);
      } finally {
        // Reset the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    };
    reader.readAsText(file);
  };

  const handleModalSubmit = async (values) => {
    try {
      if (editingItem) {
        await updateSuggestedAsync({
          id: editingItem.id,
          suggest: values.suggest,
          priority: values.priority,
        });
        setLocalSuggestedData((prev) => ({
          ...prev,
          result: prev.result.map((it) =>
            it.id === editingItem.id
              ? { ...it, suggest: values.suggest, priority: values.priority }
              : it
          ),
        }));
        setIsModalOpen(false);
        setEditingItem(null);
      } else {
        const response = await createSuggestedAsync({
          suggest: values.suggest,
          priority: values.priority,
        });
        const newItem = {
          id: response?.id,
          suggest: response.suggest,
          priority: response.priority,
          created_at: response?.created_at,
          source: response?.source,
        };

        setLocalSuggestedData((prev) => {
          const newTotal = (prev.total || 0) + 1;
          const currentPageSize = prev.result.length;

          // Nếu page hiện tại đã đầy, chuyển về page cuối để hiển thị dữ liệu mới
          if (currentPageSize >= limit) {
            const lastPage = Math.ceil(newTotal / limit);

            setSearchParams((prevParams) => ({
              ...prevParams,
              page: lastPage,
            }));
            const invalidateQueries = setTimeout(() => {
              queryClient.invalidateQueries({
                queryKey: [
                  "suggestion-data",
                  searchParams?.page,
                  searchParams?.limit,
                  searchParams?.suggest,
                ],
              });
            }, 2000);
            return () => clearTimeout(invalidateQueries);
          }

          // Nếu page hiện tại còn chỗ, thêm dữ liệu mới vào đầu
          return {
            ...prev,
            result: [newItem, ...prev.result],
            total: newTotal,
          };
        });
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  // Cấu hình cột
  const columns = [
    {
      key: "priority",
      dataIndex: "priority",
      title: <FormattedMessage id="AGENTS.HOME.TABLE_COLUMN_PRIORITY" />,
      width: "100px",
      align: "center",
      render: (text, record) => (
        <Checkbox
          checked={text === 1 || text === "1"}
          onChange={async (e) => {
            try {
              const isString = typeof record.priority === "string";
              const newPriority = e.target.checked
                ? isString
                  ? "1"
                  : 1
                : isString
                  ? "0"
                  : 0;
              await updateSuggestedAsync({
                id: record.id,
                suggest: record.suggest,
                priority: newPriority,
              });
              setLocalSuggestedData((prev) => ({
                ...prev,
                result: prev.result.map((it) =>
                  it.id === record.id ? { ...it, priority: newPriority } : it
                ),
              }));
            } catch {
              toast.error("Có lỗi khi cập nhật ưu tiên!");
            }
          }}
        />
      ),
    },
    {
      key: "suggest",
      dataIndex: "suggest",
      title: <FormattedMessage id="AGENTS.HOME.TABLE_COLUMN_SUGGEST" />,
      type: "text",
      width: "400px",
      align: "left",
    },
    {
      key: "created_at",
      dataIndex: "created_at",
      title: <FormattedMessage id="AGENTS.HOME.TABLE_COLUMN_CREATED_DATE" />,
      type: "text",
      width: "150px",
      align: "center",
      render: (text) => <p>{convertToVNTime(text)}</p>,
    },
    {
      key: "source",
      dataIndex: "source",
      title: <FormattedMessage id="AGENTS.HOME.TABLE_COLUMN_SOURCE" />,
      type: "text",
      width: "150px",
      align: "center",
    },
    {
      key: "action",
      dataIndex: "action",
      title: <FormattedMessage id="AGENTS.REFERENCE_DATA.ACTION" />,
      type: "actions",
      width: "100px",
      align: "center",
      render: (_value, record) => (
        <div className="flex gap-4 justify-center ">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record);
            }}
            className="text-blue-500 hover:text-blue-300"
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </button>
          <Popconfirm
            title={<FormattedMessage id="AGENTS.SUGGESTED.DELETE_TASK" />}
            description={
              <FormattedMessage id="AGENTS.SUGGESTED.DELETE_CONFIRM" />
            }
            onConfirm={() => handleDelete(record)}
            // onCancel={cancelDelete}
            okText={<FormattedMessage id="AGENTS.REFERENCE_DATA.YES" />}
            cancelText={<FormattedMessage id="AGENTS.REFERENCE_DATA.NO" />}
          >
            <button className="text-red-500 hover:text-red-300">
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${intl.formatMessage({ id: "AGENTS.SUGGESTED.MANAGE_TITLE" })}`}
      width="90vw"
      // className="mt-[500px]"
      maxWidth="100vw"
    >
      <div className="">
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept=".csv,.txt"
          onChange={handleFileChange}
        />
        <div className="flex flex-col justify-between md:flex-row md:items-center mb-4 gap-4">
          <div className="flex gap-3">
            <Input
              placeholder={intl.formatMessage({
                id: "AGENTS.TABLE_TOOLBAR.SEARCH",
              })}
              prefix={
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  className="text-gray-400"
                />
              }
              allowClear
              className="w-full md:w-80"
              name="suggest"
              value={searchDraft.suggest}
              onChange={handleSearchChange}
              onPressEnter={handleSearch}
              onClear={() =>
                handleSearchChange(
                  { target: { name: "suggest", value: "" } },
                  true
                )
              }
            />
            <button
              className="px-4 py-1.5 bg-green-500 hover:bg-green-400 text-white rounded-lg flex items-center gap-2 whitespace-nowrap"
              onClick={handleSearch}
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <FormattedMessage id="AGENTS.TABLE_TOOLBAR.SEARCH" />
            </button>

            <div className="flex flex-wrap items-center gap-2">
              <button
                className="px-4 py-1.5 bg-[#7239EA] text-white rounded-lg flex items-center gap-2 whitespace-nowrap"
                onClick={handleCreate}
              >
                <FontAwesomeIcon icon={faPlus} />{" "}
                <FormattedMessage id="AGENTS.HOME.CREATE" />
              </button>
              <button
                className="px-4 py-1.5 bg-green-500 text-white rounded-lg flex items-center gap-2 whitespace-nowrap"
                onClick={handleImportCSV}
              >
                <FontAwesomeIcon icon={faFileCsv} />{" "}
                <FormattedMessage id="AGENTS.HOME.IMPORT_CSV" />
              </button>
              <Tooltip
                title={
                  <span>
                    <FormattedMessage id="AGENTS.REFERENCE_DATA.DOWNLOAD_TEMPLATE" />{" "}
                    <a
                      href={
                        "https://media.chatme.vn/export_temp/temp_suggestions.csv"
                      }
                      rel="noopener noreferrer"
                      className="text-blue-500 underline-line"
                    >
                      <FormattedMessage id="AGENTS.REFERENCE_DATA.HERE" />
                    </a>
                  </span>
                }
                placement="topLeft"
              >
                <KeenIcon
                  icon="question"
                  className="text-2xl text-gray-500 cursor-help"
                />
              </Tooltip>
            </div>
          </div>
          <div className="flex">
            <TablePagination
              current={page}
              pageSize={limit}
              total={localSuggestedData?.total || 0}
              onChange={handlePageChange}
              onShowSizeChange={handlePageSizeChange}
              pageSizeOptions={["5", "10", "20", "50", "80", "100"]}
            />
          </div>
        </div>
        <Table
          dataSource={localSuggestedData?.result}
          loading={suggestionDataLoading}
          columns={columns}
          rowKey="id"
          pagination={false}
          scroll={{ x: "max-content" }}
          bordered
          size="large"
          locale={{
            emptyText: <FormattedMessage id="AGENTS.REFERENCE_DATA.NO_DATA" />,
          }}
          className="custom-table"
          onRow={() => ({
            className: "hover:bg-gray-50",
          })}
          {...tableProps}
        />
        <div className="flex justify-end mt-3 flex-wrap gap-3 mb-4 items-center">
          <div className="flex">
            <TablePagination
              current={page}
              pageSize={limit}
              total={localSuggestedData?.total || 0}
              onChange={handlePageChange}
              onShowSizeChange={handlePageSizeChange}
              pageSizeOptions={["5", "10", "20", "50", "80", "100"]}
            />
          </div>
        </div>
        <SuggestedModal
          isEditing={!!editingItem}
          open={isModalOpen}
          onCancel={handleModalCancel}
          onFinish={handleModalSubmit}
          initialValues={
            editingItem
              ? { suggest: editingItem.suggest, priority: editingItem.priority }
              : null
          }
        />
      </div>
    </Modal>
  );
};

ManageSuggested.propTypes = {
  tableProps: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export { ManageSuggested };
